import type { Call } from './actions'
import { toCallKey } from './actions'

const STORAGE_KEY = 'dontpanic.multicall.immutable.v1'
const MAX_AGE_MS = 24 * 60 * 60_000

const IMMUTABLE_METHOD_ALLOWLIST = new Set(['token0', 'token1', 'name', 'symbol', 'decimals'])

type CachedCallResult = {
  data: string
  blockNumber: number
  cachedAt: number
}

type CacheStore = Record<string, CachedCallResult>

function storageKey(chainId: number): string {
  return `${STORAGE_KEY}:${chainId}`
}

function readStore(chainId: number): CacheStore {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(storageKey(chainId))
    if (!raw) return {}
    return JSON.parse(raw) as CacheStore
  } catch {
    return {}
  }
}

function writeStore(chainId: number, store: CacheStore): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(storageKey(chainId), JSON.stringify(store))
  } catch {
    /* quota */
  }
}

function methodFromCallData(callData: string): string | undefined {
  if (!callData || callData.length < 10) return undefined
  const selector = callData.slice(0, 10).toLowerCase()
  const known: Record<string, string> = {
    '0x0dfe1681': 'token0',
    '0xd21220a7': 'token1',
    '0x06fdde03': 'name',
    '0x95d89b41': 'symbol',
    '0x313ce567': 'decimals',
  }
  return known[selector]
}

export function isImmutableMulticallMethod(methodName: string | undefined): boolean {
  return Boolean(methodName && IMMUTABLE_METHOD_ALLOWLIST.has(methodName))
}

export function readImmutableCallCache(
  chainId: number | undefined,
  call: Call | undefined,
): CachedCallResult | undefined {
  if (!chainId || !call?.callData) return undefined
  const method = methodFromCallData(call.callData)
  if (!isImmutableMulticallMethod(method)) return undefined

  const entry = readStore(chainId)[toCallKey(call)]
  if (!entry) return undefined
  if (Date.now() - entry.cachedAt > MAX_AGE_MS) return undefined
  if (!entry.data || entry.data === '0x') return undefined
  return entry
}

export function writeImmutableCallCache(
  chainId: number | undefined,
  call: Call | undefined,
  data: string | undefined,
  blockNumber: number | undefined,
): void {
  if (!chainId || !call?.callData || !data || data === '0x' || blockNumber == null) return
  const method = methodFromCallData(call.callData)
  if (!isImmutableMulticallMethod(method)) return

  const store = readStore(chainId)
  store[toCallKey(call)] = { data, blockNumber, cachedAt: Date.now() }
  writeStore(chainId, store)
}

export function clearImmutableCallCache(chainId?: number): void {
  if (typeof window === 'undefined') return
  if (chainId == null) {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(STORAGE_KEY))
      .forEach((key) => window.sessionStorage.removeItem(key))
    return
  }
  window.sessionStorage.removeItem(storageKey(chainId))
}
